/**
 * Common Commands — shared command definitions for Editor and Web apps.
 *
 * Returns an array of command descriptors ready for `registerCommand()`.
 * Pass `openPalette` to bridge local component state (e.g. setPaletteOpen).
 */

import type { Command } from "../lib/commands";
import { getPlatformService } from "../platform";
import {
    getPaneLayoutLeafIds,
    getSidebarPresentation,
    useEditorStore,
} from "../store/editor-store";
import { useLogStore } from "../store/log-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { useRecentStore } from "../store/recent-store";
import { useSettingsStore } from "../store/settings-store";
import { useThemeStore } from "../theme/runtime/theme-store";

export interface CommonCommandCallbacks {
    /** Called to open the command palette (optional when using AppShell, which registers its own) */
    openPalette?: () => void;
    /** Optional translator for command labels/categories. */
    t?: (key: string) => string;
}

/**
 * Returns all commands shared between Editor and Web.
 * Editor-only commands (e.g. file.openFolder) should be registered separately.
 */
export function getCommonCommands(
    callbacks: CommonCommandCallbacks = {},
): Command[] {
    const text = (key: string, fallback: string) => callbacks.t?.(key) ?? fallback;
    const category = (key: string, fallback: string) =>
        text(`category.${key}`, fallback);
    const label = (key: string, fallback: string) =>
        text(`command.${key}`, fallback);

    async function persistEditorUserSetting(
        dotPath: string,
        value: unknown,
    ): Promise<void> {
        try {
            await getPlatformService().settings.setValue({
                owner: "editor",
                target: "user",
                dotPath,
                value,
            });
            await useSettingsStore.getState().loadSnapshot();
        } catch {
            // Ignore when the platform service is not yet initialized or persistence fails.
        }
    }

    return [
        // ── File commands ──
        {
            id: "file.new",
            label: label("file.new", "New File"),
            shortcut: "Cmd+N",
            category: category("file", "File"),
            execute: () => useEditorStore.getState().addTab(),
        },
        {
            id: "file.open",
            label: label("file.open", "Open File"),
            shortcut: "Cmd+O",
            category: category("file", "File"),
            execute: () => useEditorStore.getState().openFile(),
        },
        {
            id: "workspace.open",
            label: label("workspace.open", "Open Workspace"),
            shortcut: "Cmd+Shift+W",
            category: category("file", "File"),
            execute: () => useEditorStore.getState().loadWorkspace(),
        },
        {
            id: "workspace.save",
            label: label("workspace.save", "Save Workspace"),
            category: category("file", "File"),
            execute: () => useEditorStore.getState().saveWorkspace(),
        },
        {
            id: "workspace.close",
            label: label("workspace.close", "Close Workspace"),
            category: category("file", "File"),
            execute: () => useEditorStore.getState().closeWorkspace(),
        },
        {
            id: "file.clearRecent",
            label: label("file.clearRecent", "Clear Recent Items"),
            category: category("file", "File"),
            execute: () => useRecentStore.getState().clearAll(),
        },
        {
            id: "file.save",
            label: label("file.save", "Save"),
            shortcut: "Cmd+S",
            category: category("file", "File"),
            execute: () => useEditorStore.getState().saveFile(),
        },
        {
            id: "file.saveAs",
            label: label("file.saveAs", "Save As..."),
            shortcut: "Cmd+Shift+S",
            category: category("file", "File"),
            execute: () => useEditorStore.getState().saveFileAs(),
        },
        {
            id: "file.closeTab",
            label: label("file.closeTab", "Close Tab"),
            category: category("file", "File"),
            execute: () => {
                const s = useEditorStore.getState();
                s.closeTab(s.getFocusedTabId());
            },
        },
        {
            id: "file.closeAllTabs",
            label: label("file.closeAllTabs", "Close All Tabs"),
            category: category("file", "File"),
            execute: () => {
                const s = useEditorStore.getState();
                const ids = s.tabs.map((t) => t.id);
                for (const id of ids) s.closeTab(id);
            },
        },
        {
            id: "file.nextTab",
            label: label("file.nextTab", "Next Tab"),
            shortcut: "Cmd+Shift+]",
            category: category("file", "File"),
            execute: () => {
                const s = useEditorStore.getState();
                const focusedTabId = s.getFocusedTabId();
                const idx = s.tabs.findIndex((t) => t.id === focusedTabId);
                if (idx >= 0 && s.tabs.length > 1) {
                    s.setActiveTab(
                        s.tabs[(idx + 1) % s.tabs.length].id,
                    );
                }
            },
        },
        {
            id: "file.prevTab",
            label: label("file.prevTab", "Previous Tab"),
            shortcut: "Cmd+Shift+[",
            category: category("file", "File"),
            execute: () => {
                const s = useEditorStore.getState();
                const focusedTabId = s.getFocusedTabId();
                const idx = s.tabs.findIndex((t) => t.id === focusedTabId);
                if (idx >= 0 && s.tabs.length > 1) {
                    s.setActiveTab(
                        s.tabs[(idx - 1 + s.tabs.length) % s.tabs.length].id,
                    );
                }
            },
        },

        // ── View commands ──
        {
            id: "view.editor",
            label: label("view.editor", "Editor Only"),
            category: category("view", "View"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setPaneViewMode(s.focusedPaneId, "editor");
            },
        },
        {
            id: "view.split",
            label: label("view.split", "Split View"),
            category: category("view", "View"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setPaneViewMode(s.focusedPaneId, "split");
            },
        },
        {
            id: "view.preview",
            label: label("view.preview", "Preview Only"),
            category: category("view", "View"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setPaneViewMode(s.focusedPaneId, "preview");
            },
        },
        {
            id: "view.toggleSidebar",
            label: label("view.toggleSidebar", "Toggle Sidebar"),
            shortcut: "Cmd+B",
            category: category("view", "View"),
            execute: () => useEditorStore.getState().toggleSidebar(),
        },
        {
            id: "view.toggleRightSidebar",
            label: label("view.toggleRightSidebar", "Toggle Right Sidebar"),
            shortcut: "Cmd+Shift+B",
            category: category("view", "View"),
            execute: () => useEditorStore.getState().toggleRightSidebar(),
        },
        {
            id: "view.toggleDarkMode",
            label: label("view.toggleDarkMode", "Toggle Theme"),
            category: category("view", "View"),
            execute: () => void useThemeStore.getState().toggleColorTheme(),
        },
        {
            id: "view.toggleLogPanel",
            label: label("view.toggleLogPanel", "Toggle Log Panel"),
            shortcut: "Cmd+J",
            category: category("view", "View"),
            execute: () => useLogStore.getState().togglePanel(),
        },
        {
            id: "view.toggleWidgetMode",
            label: label("view.toggleWidgetMode", "Toggle Widget Mode (L0 <-> L2)"),
            shortcut: "Cmd+Alt+0",
            category: category("view", "View"),
            execute: async () => {
                const store = useEditorStore.getState();
                const nextMode =
                    store.codeMirrorWidgetMode === "pretty" ? "raw" : "pretty";
                store.setCodeMirrorWidgetMode(nextMode);
                await persistEditorUserSetting(
                    "editor.codeMirror.widgetMode",
                    nextMode,
                );
            },
        },
        {
            id: "view.toggleSyntaxHoverDetail",
            label: label("view.toggleSyntaxHoverDetail", "Toggle Syntax Hover Detail"),
            category: category("view", "View"),
            execute: async () => {
                const store = useEditorStore.getState();
                const nextEnabled = !store.syntaxHoverDetailEnabled;
                store.toggleSyntaxHoverDetail();
                await persistEditorUserSetting(
                    "editor.codeMirror.hoverDetail",
                    nextEnabled,
                );
            },
        },

        // ── General ──
        {
            id: "palette.open",
            label: label("palette.open", "Command Palette"),
            shortcut: "Cmd+Shift+P",
            category: category("general", "General"),
            execute: () => callbacks.openPalette?.(),
        },
        {
            id: "settings.open",
            label: label("settings.open", "Open Settings"),
            shortcut: "Cmd+,",
            category: category("general", "General"),
            execute: () =>
                useEditorStore.getState().setSidebarView("settings"),
        },

        // ── Plugin commands ──
        {
            id: "plugin.runAll",
            label: label("plugin.runAll", "Run All Plugins"),
            shortcut: "Cmd+Shift+Enter",
            category: category("plugin", "Plugin"),
            execute: () => {
                const store = usePluginExecutionStore.getState();
                if (store.isRunningAll) {
                    store.cancelRunAll();
                } else {
                    const s = useEditorStore.getState();
                    const focusedTabId = s.getFocusedTabId();
                    const doc = s.parsedDocumentMap.get(focusedTabId) ?? s.parsedDocument;
                    if (doc) {
                        store.runAll(doc.definitions);
                    }
                }
            },
        },
        {
            id: "plugin.stopAll",
            label: label("plugin.stopAll", "Stop All Plugins"),
            shortcut: "Escape",
            category: category("plugin", "Plugin"),
            execute: () => {
                // Escape: close detail view overlay first, then stop plugins
                const s = useEditorStore.getState();
                if (
                    s.selectedPluginName ||
                    s.selectedInstanceId ||
                    s.selectedSidecarId ||
                    s.selectedExtensionId ||
                    s.showEngineDetail ||
                    s.showMcpDetail ||
                    s.showSessionDetail ||
                    (s.sidebarOpen &&
                        getSidebarPresentation(s.sidebarView) === "content")
                ) {
                    if (
                        s.sidebarOpen &&
                        getSidebarPresentation(s.sidebarView) === "content"
                    ) {
                        useEditorStore.setState({ sidebarOpen: false });
                    }
                    s.closeAllDetailViews();
                    return;
                }
                const store = usePluginExecutionStore.getState();
                if (store.isRunningAll) {
                    store.cancelRunAll();
                }
            },
        },
        {
            id: "plugin.clearAll",
            label: label("plugin.clearAll", "Clear All Outputs"),
            category: category("plugin", "Plugin"),
            execute: () => usePluginExecutionStore.getState().clearAll(),
        },

        // ── Sidebar views ──
        {
            id: "sidebar.explorer",
            label: label("sidebar.explorer", "Show Explorer"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("explorer");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "sidebar.files",
            label: label("sidebar.files", "Show Files"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("files");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "sidebar.toc",
            label: label("sidebar.toc", "Show Table of Contents"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("toc");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "sidebar.plugins",
            label: label("sidebar.plugins", "Show Plugin Instances in Explorer"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                useEditorStore.setState({
                    sidebarOpen: true,
                    sidebarView: "explorer",
                    selectedPluginName: null,
                    selectedSidecarId: null,
                    selectedExtensionId: null,
                    showEngineDetail: false,
                    showMcpDetail: false,
                    showSessionDetail: false,
                });
            },
        },
        {
            id: "sidebar.installedPlugins",
            label: label("sidebar.installedPlugins", "Show Installed Plugins"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("engine");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "sidebar.engine",
            label: label("sidebar.engine", "Show Engine"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("engine");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "sidebar.sidecars",
            label: label("sidebar.sidecars", "Show Sidecars"),
            category: category("sidebar", "Sidebar"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("engine");
                if (!s.sidebarOpen) s.toggleSidebar();
            },
        },
        {
            id: "search.focus",
            label: label("search.focus", "Search in Files"),
            shortcut: "Cmd+Shift+F",
            category: category("search", "Search"),
            execute: () => {
                const s = useEditorStore.getState();
                s.setSidebarView("search");
                if (!s.sidebarOpen) s.toggleSidebar();
                // Dispatch focus event for SearchInput to pick up
                window.dispatchEvent(new Event("magic-md:search-focus"));
            },
        },

        // ── Pane commands ──
        {
            id: "pane.splitRight",
            label: label("pane.splitRight", "Split Pane Right"),
            shortcut: "Cmd+\\",
            category: category("pane", "Pane"),
            execute: () => useEditorStore.getState().splitPane("horizontal"),
        },
        {
            id: "pane.splitDown",
            label: label("pane.splitDown", "Split Pane Down"),
            category: category("pane", "Pane"),
            execute: () => useEditorStore.getState().splitPane("vertical"),
        },
        {
            id: "pane.close",
            label: label("pane.close", "Close Pane"),
            category: category("pane", "Pane"),
            execute: () => {
                const s = useEditorStore.getState();
                if (s.panes.length > 1) {
                    s.closePane(s.focusedPaneId);
                }
            },
        },
        {
            id: "pane.focusNext",
            label: label("pane.focusNext", "Focus Next Pane"),
            shortcut: "Cmd+]",
            category: category("pane", "Pane"),
            execute: () => {
                const s = useEditorStore.getState();
                if (s.panes.length <= 1) return;
                const expandedPaneIds = new Set(
                    s.paneLayout
                        ? getPaneLayoutLeafIds(s.paneLayout)
                        : s.panes.map((p) => p.paneId),
                );
                const panes = s.panes.filter((p) => expandedPaneIds.has(p.paneId));
                if (panes.length <= 1) return;
                const idx = panes.findIndex((p) => p.paneId === s.focusedPaneId);
                const next = panes[(idx + 1) % panes.length];
                s.setFocusedPane(next.paneId);
            },
        },
        {
            id: "pane.focusPrev",
            label: label("pane.focusPrev", "Focus Previous Pane"),
            shortcut: "Cmd+[",
            category: category("pane", "Pane"),
            execute: () => {
                const s = useEditorStore.getState();
                if (s.panes.length <= 1) return;
                const expandedPaneIds = new Set(
                    s.paneLayout
                        ? getPaneLayoutLeafIds(s.paneLayout)
                        : s.panes.map((p) => p.paneId),
                );
                const panes = s.panes.filter((p) => expandedPaneIds.has(p.paneId));
                if (panes.length <= 1) return;
                const idx = panes.findIndex((p) => p.paneId === s.focusedPaneId);
                const prev = panes[(idx - 1 + panes.length) % panes.length];
                s.setFocusedPane(prev.paneId);
            },
        },
    ];
}
